import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { getFrameLikeElements } from "@excalidraw/element";
import { useCallback, useEffect, useState } from "react";

import { useSetAtom } from "../../app-jotai";
import { presentationAtom } from "../../data/workspace";

import "./Workspace.scss";

interface FrameInfo {
  id: string;
  name: string;
}

export const PresentationPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const setPresentation = useSetAtom(presentationAtom);
  const [frames, setFrames] = useState<FrameInfo[]>([]);

  const computeFrames = useCallback((): FrameInfo[] => {
    if (!excalidrawAPI) {
      return [];
    }
    const frameEls = getFrameLikeElements(excalidrawAPI.getSceneElements());
    return (
      [...frameEls]
        // slide order: top-to-bottom, then left-to-right
        .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y))
        .map((f, i) => ({ id: f.id, name: f.name || `Frame ${i + 1}` }))
    );
  }, [excalidrawAPI]);

  useEffect(() => {
    setFrames(computeFrames());
  }, [computeFrames]);

  const start = (index: number) => {
    const fresh = computeFrames();
    if (!fresh.length) {
      return;
    }
    setFrames(fresh);
    setPresentation({ frameIds: fresh.map((f) => f.id), index });
  };

  return (
    <div className="WorkspacePanel">
      <div className="WorkspacePanel__title">Presentation</div>
      <p className="WorkspacePanel__hint">
        Turn the frames on your canvas into slides. Each frame becomes one
        slide, ordered top-to-bottom.
      </p>

      <FilledButton
        size="medium"
        label="▶ Start presentation"
        onClick={() => start(0)}
        disabled={frames.length === 0}
      />
      <FilledButton
        size="medium"
        variant="outlined"
        label="Refresh frames"
        onClick={() => setFrames(computeFrames())}
      />

      {frames.length === 0 ? (
        <p className="WorkspacePanel__hint">
          No frames yet. Use the Frame tool (press <kbd>F</kbd>) to add slides.
        </p>
      ) : (
        <div className="WorkspaceFrameList">
          {frames.map((f, i) => (
            <div
              key={f.id}
              className="WorkspaceFrameList__item"
              onClick={() => start(i)}
              title="Present from this slide"
            >
              <span className="WorkspaceFrameList__index">{i + 1}</span>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {f.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
